/**
 * Transaction Status Page - Standalone status page for external partner apps
 *
 * Accessed via URL params: transaction_id, token, lang, refNumber
 * Fetches transaction data from API and displays status information
 */

import { t } from "@lingui/core/macro";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import type { TransactionStatusResponse } from "@/api";
import { StatusComponent as Status } from "@/components/ramps/Status";
import { RampsText } from "@/components/ramps/Text";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { useApiClientWithAutoVersion } from "@/hooks/useApiClientWithAutoVersion";
import { useCountries } from "@/hooks/useCountries";
import {
	getApiVersionFromSearchParams,
	validateRampsSearch,
} from "@/lib/ramps-search-schema";
import { convertTransactionDataToStatusDetails } from "@/lib/status-utils";
import { useWebViewIntegration } from "@/lib/url-state";
import { rampsActions, useRampsStore } from "@/stores/use-ramps-store";
import { RampsButton } from "../components/ramps/Button";

export const Route = createFileRoute("/transaction-status")({
	validateSearch: (search) =>
		search as {
			transaction_id: string;
			token?: string;
			accesstoken?: string;
			lang?: string;
			refNumber?: string; // Make refNumber optional
			wallet_provider?: string;
		},
	component: TransactionStatusPage,
});

function TransactionStatusPage() {
	const navigate = useNavigate();
	const search = Route.useSearch();
	const store = useRampsStore();
	const { sendToWebView } = useWebViewIntegration();

	// Get auth token (supports v2, v3, and v5 API)
	const authToken = search.accesstoken || search.token || "";

	// Get transaction ID from URL or store (v3 may have it from prepare endpoint)
	const transactionId = search.transaction_id || store.transactionId;

	// Set the token in the store so useCountries can access it
	if (authToken) {
		rampsActions.setToken(authToken);
	}

	// Detect API version for standalone status page
	// v2: JWT token + refNumber in URL (or context params)
	// v3: JWT token without refNumber/context params
	// v5: OAuth token (accesstoken)
	useEffect(() => {
		if (authToken) {
			const validatedSearch = validateRampsSearch({
				...search,
				transaction_id: transactionId || "",
			});
			const detectedVersion = getApiVersionFromSearchParams(validatedSearch);
			if (detectedVersion !== store.apiVersion) {
				rampsActions.setApiVersion(detectedVersion);
				if (import.meta.env.DEV) {
					const versionSource =
						detectedVersion === "v5"
							? "accesstoken (OAuth)"
							: detectedVersion === "v2"
								? search.refNumber
									? "token + refNumber (JWT v2 standalone)"
									: "token + URL context params (JWT v2)"
								: "token only (JWT v3)";
					console.log(
						`🔍 Transaction Status Page - API Version detected: ${detectedVersion} from: ${versionSource}`,
					);
				}
			}
		}
	}, [authToken, search, transactionId, store.apiVersion]);

	const { apiClient } = useApiClientWithAutoVersion({
		authToken: authToken || "",
	});

	// Detect API version for prepareStatus call
	const validatedSearch = validateRampsSearch({
		...search,
		transaction_id: transactionId || "",
	});
	const apiVersion = getApiVersionFromSearchParams(validatedSearch);
	const isV3 = apiVersion === "v3";

	// Prepare status for v3 API (called before GET status)
	const {
		data: prepareStatusData,
		isLoading: isPreparingStatus,
		error: prepareStatusError,
	} = useQuery({
		queryKey: ["prepare-status", transactionId, authToken],
		queryFn: async () => {
			return await apiClient.transaction.prepareStatus();
		},
		enabled: isV3 && !!transactionId && !!authToken,
	});

	// Fetch transaction data
	// For v2: refNumber is in URL params (standalone status page pattern)
	// For v3: refNumber is not in URL, API returns referenceNumber in response
	//   - First call prepareStatus, then get status
	// For v5: refNumber may be in URL params
	const {
		data: transactionData,
		isLoading: isLoadingStatus,
		error,
	} = useQuery({
		queryKey: [
			"transaction-status",
			transactionId,
			search.refNumber || "no-ref",
			isV3 ? prepareStatusData?.transactionId : undefined,
		],
		queryFn: async () => {
			return (await apiClient.transaction.getTransactionStatus(
				transactionId,
				search.refNumber, // Optional - v3 doesn't require it, v5 may have it
				authToken || "",
			)) as TransactionStatusResponse;
		},
		enabled:
			!!transactionId &&
			!!authToken &&
			(!isV3 || (!!prepareStatusData && !prepareStatusError)), // For v3, wait for prepareStatus and ensure no error
	});

	const isLoading = isPreparingStatus || isLoadingStatus;
	const hasError = prepareStatusError || error;
	// Fetch countries data and populate the store
	useCountries();

	const handleClose = () => {
		// Prepare transaction response
		const transactionResponse = {
			id: transactionId,
			status: transactionData?.transactionStatus || "unknown",
			more_info_url: "",
			stellar_transaction_id: "",
			external_transaction_id: transactionId,
		};

		// Send transaction in legacy format to parent window
		if (window.parent && window.parent !== window) {
			window.parent.postMessage(
				{
					transaction: transactionResponse,
				},
				"*",
			);
		}

		// Also use the existing WebView integration for additional compatibility
		// For deposit, use confirmationNumber; for withdraw, use referenceNumber
		const kindUpper = transactionData?.kind?.toUpperCase();
		const isDeposit = kindUpper === "DEPOSIT";
		const refNumberForWebView =
			isDeposit &&
			(transactionData?.confirmationNumber || transactionData?.confirmationCode)
				? transactionData.confirmationNumber || transactionData.confirmationCode
				: transactionData?.referenceNumber || "";

		sendToWebView("TRANSACTION_STATUS_VIEWED", {
			transactionId: transactionId,
			status: transactionData?.transactionStatus || "unknown",
			referenceNumber: refNumberForWebView,
			token: authToken || "",
			message: "Transaction status viewed",
			timestamp: new Date().toISOString(),
		});
	};

	const handleCancelTransaction = () => {
		// Navigate to cancellation flow with transaction data
		// For deposit: use confirmationNumber/confirmationCode, for withdraw: use referenceNumber
		const kindUpper = transactionData?.kind?.toUpperCase();
		const isDeposit = kindUpper === "DEPOSIT";
		const refNumber =
			search.refNumber ||
			(isDeposit &&
			(transactionData?.confirmationNumber || transactionData?.confirmationCode)
				? transactionData.confirmationNumber || transactionData.confirmationCode
				: transactionData?.referenceNumber);

		navigate({
			to: "/ramps/cancel",
			search: {
				transaction_id: transactionId,
				refNumber,
				lang: store.lang || "en",
				token: search.token,
				accesstoken: search.accesstoken,
				// biome-ignore lint/suspicious/noExplicitAny: TanStack Router requires flexible search param type
			} as any,
		});
	};

	if (isLoading) {
		return <LoadingSpinner size="lg" />;
	}

	if (hasError || !transactionData) {
		return (
			<div className="min-h-screen flex flex-col items-center justify-center px-4">
				<RampsText variant="title-lg-bold" color="error" className="mb-4">
					{t`Error Loading Transaction`}
				</RampsText>
				<RampsText
					variant="body-default"
					color="secondary"
					className="text-center mb-8"
				>
					{t`Unable to load transaction details. Please check your link and try again.`}
				</RampsText>
				<RampsButton variant="primary" onClick={handleClose}>
					{t`Go Back`}
				</RampsButton>
			</div>
		);
	}

	return (
		<div
			className="min-h-screen flex flex-col max-w-screen-sm mx-auto"
			style={{ backgroundColor: "var(--ramps-background-base)" }}
		>
			<Status
				statusDetails={convertTransactionDataToStatusDetails(transactionData)}
				onCancelTransaction={handleCancelTransaction}
			/>
		</div>
	);
}
